import React from 'react';

import { Maybe, useStripeGetPaymentHistoryQuery } from 'src/api';

interface stripeGetPaymentHistoryArgs {
  verifierId?: Maybe<string>;
  skip?: boolean;
  limit?: Maybe<number>;
}

export const useStripePaymentHistory = ({
  verifierId,
  skip = false,
  limit = 10,
}: stripeGetPaymentHistoryArgs) => {
  const [pages, setPages] = React.useState<Array<string | undefined>>([undefined]);
  const [currentPageNumber, setCurrentPageNumber] = React.useState(0);

  const { data, loading, refetch } = useStripeGetPaymentHistoryQuery({
    variables: {
      verifierId,
      pageId: pages[currentPageNumber],
      limit,
    },
    skip,
    fetchPolicy: 'no-cache',
  });

  const revenueHistoryData = React.useMemo(() => {
    return data?.stripeGetPaymentHistory.paymentHistoryCharges || [];
  }, [data?.stripeGetPaymentHistory.paymentHistoryCharges]);

  const hasNextPage = React.useMemo(() => {
    return !!data?.stripeGetPaymentHistory.nextPageId;
  }, [data?.stripeGetPaymentHistory.nextPageId]);

  React.useEffect(() => {
    const nextPageId = data?.stripeGetPaymentHistory.nextPageId ?? undefined;

    if (!pages.some(pageId => pageId === nextPageId)) {
      setPages([...pages, nextPageId]);
    }
  }, [pages, data?.stripeGetPaymentHistory.nextPageId]);

  React.useEffect(() => {
    setPages([undefined]);
    setCurrentPageNumber(0);
  }, []);

  const canGoNext = React.useMemo(
    () => currentPageNumber < pages.length - 1,
    [currentPageNumber, pages.length],
  );

  const canGoBack = React.useMemo(() => currentPageNumber > 0, [currentPageNumber]);

  const goNext = React.useCallback(() => {
    if (canGoNext) {
      setCurrentPageNumber(currentPageNumber + 1);
    }
  }, [canGoNext, currentPageNumber]);

  const goBack = React.useCallback(() => {
    if (canGoBack) {
      setCurrentPageNumber(currentPageNumber - 1);
    }
  }, [canGoBack, currentPageNumber]);

  return {
    revenueHistoryData,
    loadingRevenueHistory: loading,
    hasNextPage,
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    pageNumber: currentPageNumber + 1,
    refetch,
  };
};
